import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Crown, ShieldCheck, Mail, Download, Play, CalendarClock, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fetchVerificationQueue, verifyListing, verifyProfile, fetchSubscribers, exportSubscribersCsv, fetchAdminVideos, setVideoFeatured, BRAND_VIDEO_POSTER, fetchPendingEvents, moderateEvent } from "@/lib/api";

function VerifyRow({ item, kind, onToggle }) {
  return (
    <div className="border border-sand-200 bg-white p-5 flex items-center justify-between gap-4" data-testid={`admin-${kind}-row-${item.id}`}>
      <div className="min-w-0">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1">
          {(item.category || item.type)} · {item.city}
        </div>
        <Link
          to={kind === "listing" ? `/directory/${item.id}` : `/profile/${item.id}`}
          className="font-serif text-xl font-bold text-sand-900 hover:text-brand-500 transition-colors flex items-center gap-2"
        >
          <span className="truncate">{item.name}</span>
          {item.verified && <BadgeCheck size={16} className="text-brand-500 shrink-0" />}
        </Link>
      </div>
      <button
        onClick={() => onToggle(item)}
        data-testid={`admin-verify-toggle-${item.id}`}
        className={`shrink-0 px-4 py-2.5 font-mono text-[10px] font-bold tracking-[0.2em] uppercase transition-colors ${
          item.verified
            ? "border border-sand-300 text-sand-700 hover:border-brand-500"
            : "bg-brand-500 text-sand-50 hover:bg-brand-600"
        }`}
      >
        {item.verified ? "Unverify" : "Verify"}
      </button>
    </div>
  );
}

export default function Admin() {
  const [listings, setListings] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [videos, setVideos] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    Promise.all([fetchVerificationQueue(), fetchSubscribers().catch(() => ({ count: 0 })), fetchAdminVideos().catch(() => []), fetchPendingEvents().catch(() => [])])
      .then(([q, subs, vids, evs]) => {
        setListings(q.listings || []);
        setProfiles(q.profiles || []);
        setSubscriberCount(subs.count || 0);
        setVideos(vids || []);
        setPendingEvents(evs || []);
      })
      .catch(() => toast.error("Failed to load admin data"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportSubscribersCsv();
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "tastebuds_subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const toggleListing = async (item) => {
    const updated = await verifyListing(item.id, !item.verified);
    setListings((p) => p.map((x) => (x.id === item.id ? updated : x)));
    toast.success(updated.verified ? "Listing verified" : "Verification removed");
  };
  const toggleProfile = async (item) => {
    const updated = await verifyProfile(item.id, !item.verified);
    setProfiles((p) => p.map((x) => (x.id === item.id ? updated : x)));
    toast.success(updated.verified ? "Profile verified" : "Verification removed");
  };
  const toggleVideo = async (item) => {
    const updated = await setVideoFeatured(item.id, !item.featured);
    setVideos((p) => p.map((x) => (x.id === item.id ? updated : x)));
    toast.success(updated.featured ? "Video featured on homepage" : "Removed from homepage");
  };
  const moderate = async (item, status) => {
    await moderateEvent(item.id, status);
    setPendingEvents((p) => p.filter((x) => x.id !== item.id));
    toast.success(status === "published" ? "Event approved & published" : "Event rejected");
  };

  return (
    <div data-testid="admin-page" className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3 flex items-center gap-2">
        <Crown size={14} /> Admin Console
      </div>
      <h1 className="font-serif font-black text-4xl md:text-6xl leading-[1.02] text-sand-900 mb-12">
        Verification & access.
      </h1>

      {loading ? (
        <div className="font-mono text-sm text-sand-600">Loading…</div>
      ) : (
        <div className="space-y-16">
          <section data-testid="admin-subscribers">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
              <Mail size={20} /> Newsletter Audience
            </h2>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border border-sand-200 bg-white p-6">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-sand-600 mb-2">Total Subscribers</div>
                <div className="font-serif text-5xl font-black text-sand-900" data-testid="subscriber-count">{subscriberCount}</div>
              </div>
              <button
                onClick={onExport}
                disabled={exporting || subscriberCount === 0}
                data-testid="export-subscribers-btn"
                className="inline-flex items-center gap-2 bg-sand-900 text-sand-50 px-6 py-3 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          </section>

          <section data-testid="admin-events">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
              <CalendarClock size={20} /> Event Approvals ({pendingEvents.length})
            </h2>
            {pendingEvents.length === 0 ? (
              <p className="text-sand-700 font-light">No events awaiting review.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-events-list">
                {pendingEvents.map((ev) => (
                  <div key={ev.id} className="border border-sand-200 bg-white p-5" data-testid={`admin-event-row-${ev.id}`}>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1">
                      {ev.category} · {ev.city} · by {ev.host_name}
                    </div>
                    <Link to={`/events/${ev.id}`} className="font-serif text-xl font-bold text-sand-900 hover:text-brand-500 transition-colors">
                      {ev.name}
                    </Link>
                    <p className="mt-2 text-sm text-sand-700 font-light line-clamp-2">{ev.short_description}</p>
                    <div className="mt-4 flex items-center gap-3">
                      <button
                        onClick={() => moderate(ev, "published")}
                        data-testid={`admin-approve-event-${ev.id}`}
                        className="inline-flex items-center gap-1.5 bg-forest-500 text-sand-50 px-4 py-2.5 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-forest-700 transition-colors"
                      >
                        <Check size={13} /> Approve
                      </button>
                      <button
                        onClick={() => moderate(ev, "rejected")}
                        data-testid={`admin-reject-event-${ev.id}`}
                        className="inline-flex items-center gap-1.5 border border-sand-300 text-sand-700 px-4 py-2.5 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:border-brand-500 hover:text-brand-500 transition-colors"
                      >
                        <X size={13} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
              <ShieldCheck size={20} /> Claimed Listings ({listings.length})
            </h2>
            {listings.length === 0 ? (
              <p className="text-sand-700 font-light">No claimed listings yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-listings">
                {listings.map((l) => <VerifyRow key={l.id} item={l} kind="listing" onToggle={toggleListing} />)}
              </div>
            )}
          </section>

          <section>
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
              <ShieldCheck size={20} /> Community Profiles ({profiles.length})
            </h2>
            {profiles.length === 0 ? (
              <p className="text-sand-700 font-light">No community profiles yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-profiles">
                {profiles.map((p) => <VerifyRow key={p.id} item={p} kind="profile" onToggle={toggleProfile} />)}
              </div>
            )}
          </section>
          <section data-testid="admin-videos">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-sand-900 border-b-2 border-sand-900 pb-3 mb-8 flex items-center gap-2">
              <Play size={20} /> Featured Videos ({videos.filter((v) => v.featured).length}/{videos.length})
            </h2>
            {videos.length === 0 ? (
              <p className="text-sand-700 font-light">No videos uploaded yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-videos-list">
                {videos.map((v) => (
                  <div key={v.id} className="border border-sand-200 bg-white p-4 flex items-center gap-4" data-testid={`admin-video-row-${v.id}`}>
                    <div className="w-24 h-16 shrink-0 bg-sand-900 overflow-hidden">
                      <img src={v.thumbnail_url || BRAND_VIDEO_POSTER} alt={v.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest-500 mb-1 truncate">
                        {v.entity_name || v.provider} · {v.city}
                      </div>
                      <div className="font-serif text-lg font-bold text-sand-900 truncate flex items-center gap-2">
                        {v.title}
                        {v.featured && <BadgeCheck size={15} className="text-brand-500 shrink-0" />}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleVideo(v)}
                      data-testid={`admin-feature-toggle-${v.id}`}
                      className={`shrink-0 px-4 py-2.5 font-mono text-[10px] font-bold tracking-[0.2em] uppercase transition-colors ${
                        v.featured
                          ? "border border-sand-300 text-sand-700 hover:border-brand-500"
                          : "bg-brand-500 text-sand-50 hover:bg-brand-600"
                      }`}
                    >
                      {v.featured ? "Unfeature" : "Feature"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
